<template>
    <div class="container">
      <div class="box">
        <h2>{{ t('auth.reset.title') }}</h2>
  
        <template v-if="!success">
          <input
            v-model="password"
            type="password"
            :placeholder="t('auth.reset.new_placeholder')"
            class="input"
          />
          <input
            v-model="confirmPassword"
            type="password"
            :placeholder="t('auth.reset.confirm_placeholder')"
            class="input"
          />
          <button @click="submit" class="button">
            {{ t('auth.reset.submit') }}
          </button>
        </template>
  
        <p v-if="error" class="error">{{ error }}</p>
        <p v-if="message" class="message">{{ message }}</p>
  
        <template v-if="success">
          <button @click="goLogin" class="button" style="margin-top: 1rem;">
            {{ t('auth.reset.go_login') }}
          </button>
        </template>
  
        <template v-if="expired">
          <button @click="goForgot" class="button" style="margin-top: 1rem; background-color: #9a3412;">
            {{ t('auth.reset.new_link') }}
          </button>
        </template>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import axios from 'axios'
  
  const { t } = useI18n()
  const route = useRoute()
  const router = useRouter()
  
  const token = ref('')
  const password = ref('')
  const confirmPassword = ref('')
  const message = ref('')
  const error = ref('')
  const success = ref(false)
  const expired = ref(false)
  
  onMounted(() => {
    const authToken = localStorage.getItem('token')
    if (authToken) {
      router.push('/')
    }
  
    token.value = route.query.token || ''
  })
  
  const validatePassword = (pass) => {
    const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/
    return regex.test(pass)
  }
  
  const submit = async () => {
    error.value = ''
    message.value = ''
    expired.value = false
  
    if (password.value !== confirmPassword.value) {
      error.value = t('auth.reset.password_mismatch')
      return
    }
  
    if (!validatePassword(password.value)) {
      error.value = t('auth.reset.weak_password')
      return
    }
  
    try {
      const res = await axios.post('/auth/reset-password', {
        token: token.value,
        newPassword: password.value
      })
      message.value = res.data.message
      success.value = true
    } catch (err) {
      if (err.response?.status === 401) {
        error.value = t('auth.reset.expired')
        expired.value = true
      } else {
        error.value = t('auth.reset.generic_error')
      }
    }
  }
  
  const goLogin = () => {
    router.push('/login')
  }
  
  const goForgot = () => {
    router.push('/forgot-password')
  }
  </script>
  
  <style scoped>
  .container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
  }
  
  .box {
    background-color: #050505;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
    max-width: 400px;
    width: 100%;
    color: #ffffff;
  }
  
  h2 {
    font-size: 1rem;
    margin-bottom: 1rem;
    font-weight: 500;
  }
  
  .input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #444;
    border-radius: 4px;
    background-color: #2c2c2c;
    color: #fff;
  }
  
  .input::placeholder {
    color: #aaa;
  }
  
  .button {
    width: 100%;
    padding: 0.5rem;
    background-color: #065f46;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .button:hover {
    background-color: #047857;
  }
  
  .message {
    margin-top: 1rem;
    color: #22c55e;
  }
  .error {
    margin-top: 1rem;
    color: #dd2626;
    font-weight: 500;
  }
  </style>
  